import React, { useState } from 'react';
import { MapPin, Globe, RefreshCw, CheckCircle2, AlertTriangle, ArrowRight, ShieldCheck, Compass } from 'lucide-react';
import Button from '../../shared/Button';
import useVendorLocationAlert from '../../../hooks/useVendorLocationAlert';

const VendorLocationManagerCard = ({ onNavigateToBusinessTab }) => {
  const {
    isMismatch,
    savedLocation,
    detectedLocation,
    isDetecting,
    isUpdating,
    updateToDetectedLocation,
    refreshDetection,
  } = useVendorLocationAlert();

  const [confirmUpdateModal, setConfirmUpdateModal] = useState(false);

  const handleUpdate = async () => {
    setConfirmUpdateModal(false);
    await updateToDetectedLocation();
  };

  const savedDisplay = savedLocation.display || (savedLocation.country ? `${savedLocation.city ? savedLocation.city + ', ' : ''}${savedLocation.country}` : 'Not configured');
  const detectedDisplay = detectedLocation.display || (detectedLocation.country ? `${detectedLocation.city ? detectedLocation.city + ', ' : ''}${detectedLocation.country}` : 'Detecting...');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gradient-to-r from-gray-50/70 to-white">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-50 border border-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
            <Compass className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                Business Operating Location
              </h2>
              {isMismatch ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  <AlertTriangle className="w-3 h-3 text-amber-600" />
                  Different Location Detected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  Location Synced
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Your registered location determines where clients find you in search results and nearby service directories.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={refreshDetection}
            disabled={isDetecting}
            className="text-xs flex items-center border-gray-300 text-gray-700 hover:bg-gray-50"
            title="Re-check current GPS and IP location"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isDetecting ? 'animate-spin text-primary-600' : 'text-gray-500'}`} />
            {isDetecting ? 'Detecting...' : 'Detect Location'}
          </Button>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        {/* Mismatch Alert Box */}
        {isMismatch && (
          <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50/90 to-orange-50/70 p-4 sm:p-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-amber-950">
                    Would you like to update your business operating location?
                  </h4>
                  <p className="mt-1 text-xs sm:text-sm text-amber-800 leading-relaxed">
                    You are currently connecting from <strong className="font-semibold text-amber-950">{detectedDisplay}</strong>, but your business operating location is saved as <strong className="font-semibold text-amber-950">{savedDisplay}</strong>. We do not change your location automatically.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setConfirmUpdateModal(true)}
                  disabled={isUpdating}
                  className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm text-xs sm:text-sm font-medium"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <MapPin className="w-3.5 h-3.5 mr-1.5" />
                      Update to {detectedLocation.city || detectedLocation.country}
                    </>
                  )}
                </Button>

                {onNavigateToBusinessTab && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onNavigateToBusinessTab}
                    className="border-amber-300 text-amber-900 hover:bg-amber-100/50 text-xs sm:text-sm"
                  >
                    Custom Edit
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Location Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Registered Location Card */}
          <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Active Registered Storefront
                  </span>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                  Client Facing
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-xs text-gray-500 block">Location:</span>
                  <p className="font-semibold text-gray-900 text-base">{savedDisplay}</p>
                </div>

                {savedLocation.address && (
                  <div>
                    <span className="text-xs text-gray-500 block">Address:</span>
                    <p className="text-gray-700">{savedLocation.address}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <span className="text-xs text-gray-500 block">Country:</span>
                    <p className="font-medium text-gray-800">{savedLocation.country || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">City / State:</span>
                    <p className="font-medium text-gray-800">{savedLocation.city ? `${savedLocation.city}${savedLocation.state ? ', ' + savedLocation.state : ''}` : 'Not set'}</p>
                  </div>
                </div>

                {(savedLocation.latitude != null && savedLocation.longitude != null) && (
                  <div className="pt-1 text-xs text-gray-500 font-mono">
                    Coordinates: {parseFloat(savedLocation.latitude).toFixed(4)}, {parseFloat(savedLocation.longitude).toFixed(4)}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200/80 flex items-center justify-between">
              <span className="text-xs text-gray-500">Listed in search directory</span>
              {onNavigateToBusinessTab && (
                <button
                  type="button"
                  onClick={onNavigateToBusinessTab}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 hover:underline"
                >
                  Edit in Business Profile
                  <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Detected Live Connection Card */}
          <div className={`rounded-xl border p-4 sm:p-5 flex flex-col justify-between ${
            isMismatch ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-gray-50/40'
          }`}>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                    isMismatch ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    <Globe className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Live Connection / IP Location
                  </span>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${
                  isMismatch ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {isDetecting ? 'Detecting...' : 'Current Session'}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-xs text-gray-500 block">Detected Region:</span>
                  <p className="font-semibold text-gray-900 text-base">
                    {detectedLocation.hasDetectedLocation ? detectedDisplay : (isDetecting ? 'Detecting current location...' : 'Detection unavailable')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <span className="text-xs text-gray-500 block">Detected Country:</span>
                    <p className="font-medium text-gray-800">{detectedLocation.country || (isDetecting ? 'Detecting...' : 'N/A')}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Detected City / State:</span>
                    <p className="font-medium text-gray-800">
                      {detectedLocation.city ? `${detectedLocation.city}${detectedLocation.state ? ', ' + detectedLocation.state : ''}` : (isDetecting ? 'Detecting...' : 'N/A')}
                    </p>
                  </div>
                </div>

                {(detectedLocation.latitude != null && detectedLocation.longitude != null) && (
                  <div className="pt-1 text-xs text-gray-500 font-mono">
                    Coordinates: {parseFloat(detectedLocation.latitude).toFixed(4)}, {parseFloat(detectedLocation.longitude).toFixed(4)}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200/80 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {isMismatch ? 'Does not match storefront' : 'Matches storefront'}
              </span>
              {detectedLocation.hasDetectedLocation && (
                <button
                  type="button"
                  onClick={() => setConfirmUpdateModal(true)}
                  disabled={isUpdating}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 hover:underline"
                >
                  Apply this location
                  <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Informative Security & Transparency Note */}
        <div className="flex items-start gap-2.5 p-3.5 bg-blue-50/60 rounded-lg border border-blue-100 text-xs text-blue-900">
          <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="leading-relaxed">
            <span className="font-semibold text-blue-950">Automatic Location Protection: </span>
            Vendalist will <strong>never automatically change</strong> your registered business location when you travel abroad, move temporarily, or browse via VPN. Your business always remains listed in your chosen operating region unless you explicitly choose to change it here.
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmUpdateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => !isUpdating && setConfirmUpdateModal(false)}
            />
            <div className="relative transform overflow-hidden rounded-xl bg-white p-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Update Business Location?
                  </h3>
                  <p className="text-xs text-gray-500">
                    Confirm change to your primary operating storefront
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-600 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p>
                  You are about to change your business operating location:
                </p>
                <div className="flex items-center justify-between text-xs sm:text-sm py-1 border-b border-gray-200">
                  <span className="text-gray-500">From:</span>
                  <span className="font-semibold text-gray-900">{savedDisplay}</span>
                </div>
                <div className="flex items-center justify-between text-xs sm:text-sm py-1">
                  <span className="text-gray-500">To:</span>
                  <span className="font-semibold text-amber-700">{detectedDisplay}</span>
                </div>
                <p className="text-xs text-gray-500 pt-2">
                  Clients in {detectedLocation.city || detectedLocation.country} will now be able to discover your services in their local searches.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmUpdateModal(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Confirm Location Change'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLocationManagerCard;
